import { useState, useEffect, useCallback, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import PlatformBadge from './components/PlatformBadge';
import MktPostDrawer from './MktPostDrawer';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';
import { listMktPosts } from 'src/actions/v2/marketing/analytics/v2_actionMktPosts';

const STATUS_MAP = {
    draft: { label: 'Rascunho', className: d.statusBadge.draft },
    scheduled: { label: 'Agendado', className: d.statusBadge.scheduled },
    published: { label: 'Publicado', className: d.statusBadge.published },
    failed: { label: 'Falhou', className: d.statusBadge.failed }
};

const DAYS_OF_WEEK = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const PLATFORM_DOT_COLOR = {
    instagram: 'bg-pink-500',
    facebook: 'bg-blue-500',
    google_ads: 'bg-amber-500'
};

function truncateText(text, maxLen = 80) {
    if (!text) return '';
    return text.length > maxLen ? `${text.slice(0, maxLen)}...` : text;
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return String(num);
}

function getCalendarDays(year, month) {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Previous month padding
    const prevMonthLast = new Date(year, month, 0).getDate();
    for (let i = startDow - 1; i >= 0; i--) {
        days.push({ day: prevMonthLast - i, current: false });
    }

    // Current month
    for (let i = 1; i <= daysInMonth; i++) {
        days.push({ day: i, current: true });
    }

    // Next month padding
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
        days.push({ day: i, current: false });
    }

    return days;
}

function isToday(year, month, day) {
    const now = new Date();
    return now.getFullYear() === year && now.getMonth() === month && now.getDate() === day;
}

export default function MktPosts() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('list');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    });

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listMktPosts(user.clienteId);
            setPosts(data || []);
        } catch (err) {
            console.error('Erro ao carregar publicações:', err);
        } finally {
            setLoading(false);
        }
    }, [user.clienteId]);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    const handleDrawerSave = () => {
        setDrawerOpen(false);
        fetchPosts();
    };

    const calendarDays = useMemo(
        () => getCalendarDays(currentMonth.year, currentMonth.month),
        [currentMonth.year, currentMonth.month]
    );

    const postsByDay = useMemo(() => {
        const map = {};
        posts.forEach((post) => {
            if (!post.scheduled_date) return;
            const date = new Date(post.scheduled_date);
            if (date.getFullYear() === currentMonth.year && date.getMonth() === currentMonth.month) {
                const day = date.getDate();
                if (!map[day]) map[day] = [];
                map[day].push(post);
            }
        });
        return map;
    }, [posts, currentMonth.year, currentMonth.month]);

    const navigateMonth = (direction) => {
        setCurrentMonth((prev) => {
            let newMonth = prev.month + direction;
            let newYear = prev.year;
            if (newMonth < 0) { newMonth = 11; newYear -= 1; }
            if (newMonth > 11) { newMonth = 0; newYear += 1; }
            return { year: newYear, month: newMonth };
        });
    };

    return (
        <div className={d.page.container}>
            {/* Header */}
            <div className={d.page.header.wrapper}>
                <div className={d.page.header.left}>
                    <div className={d.page.header.iconBox}>
                        <Iconify icon="eva:edit-2-outline" width={28} className={d.page.header.icon} />
                    </div>
                    <div>
                        <h1 className={d.page.header.title}>Publicações</h1>
                        <p className={d.page.header.subtitle}>Gerencie e agende suas publicações nas redes sociais</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {/* View Toggle */}
                    <div className={d.tabs.container}>
                        <button
                            className={viewMode === 'list' ? d.tabs.tabActive : d.tabs.tab}
                            onClick={() => setViewMode('list')}
                        >
                            <Iconify icon="eva:list-outline" width={16} className="inline mr-1" />
                            Lista
                        </button>
                        <button
                            className={viewMode === 'calendar' ? d.tabs.tabActive : d.tabs.tab}
                            onClick={() => setViewMode('calendar')}
                        >
                            <Iconify icon="eva:calendar-outline" width={16} className="inline mr-1" />
                            Calendário
                        </button>
                    </div>
                    <button className={d.page.header.actionButton} onClick={() => setDrawerOpen(true)}>
                        <Iconify icon="eva:plus-outline" width={18} />
                        Nova Publicação
                    </button>
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center py-20">
                    <div className="w-48 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full w-1/2 bg-[#7C3AED] rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" />
                    </div>
                </div>
            )}

            {/* List View */}
            {!loading && viewMode === 'list' && posts.length > 0 && (
                <div className={d.table.container}>
                    <div className={d.table.header}>
                        <span className={d.table.headerTitle}>Todas as publicações</span>
                        <span className="text-xs text-gray-500">{posts.length} publicações</span>
                    </div>
                    {posts.map((post) => {
                        const status = STATUS_MAP[post.status] || STATUS_MAP.draft;
                        return (
                            <div key={post._id} className={d.table.row}>
                                <div className="flex-1 min-w-0 mr-4">
                                    <p className={`${d.table.cell} truncate`}>{truncateText(post.content)}</p>
                                </div>
                                <div className="flex items-center gap-1.5 mr-4 w-40 flex-shrink-0">
                                    {(post.platforms || []).map((p) => (
                                        <PlatformBadge key={p} platform={p} />
                                    ))}
                                </div>
                                <div className="w-36 flex-shrink-0 mr-4">
                                    <p className={d.table.cellMuted}>{formatDate(post.scheduled_date)}</p>
                                </div>
                                <div className="w-24 flex-shrink-0 mr-4">
                                    <span className={status.className}>
                                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                        {status.label}
                                    </span>
                                </div>
                                <div className="w-28 flex-shrink-0 text-right">
                                    {post.status === 'published' && post.performance ? (
                                        <div className="flex items-center gap-3 justify-end">
                                            <span className={d.table.cellMuted} title="Curtidas">
                                                <Iconify icon="eva:heart-outline" width={12} className="inline mr-0.5" />
                                                {formatNumber(post.performance.likes)}
                                            </span>
                                            <span className={d.table.cellMuted} title="Alcance">
                                                <Iconify icon="eva:eye-outline" width={12} className="inline mr-0.5" />
                                                {formatNumber(post.performance.reach)}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className={d.table.cellMuted}>-</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Calendar View */}
            {!loading && viewMode === 'calendar' && (
                <div className={d.calendar.container}>
                    {/* Calendar Header */}
                    <div className={d.calendar.header}>
                        <button className={d.calendar.navBtn} onClick={() => navigateMonth(-1)}>
                            <Iconify icon="eva:arrow-ios-back-outline" width={18} className="text-gray-600" />
                        </button>
                        <span className={d.calendar.monthTitle}>
                            {MONTHS[currentMonth.month]} {currentMonth.year}
                        </span>
                        <button className={d.calendar.navBtn} onClick={() => navigateMonth(1)}>
                            <Iconify icon="eva:arrow-ios-forward-outline" width={18} className="text-gray-600" />
                        </button>
                    </div>

                    {/* Day of week headers */}
                    <div className={d.calendar.grid}>
                        {DAYS_OF_WEEK.map((dow) => (
                            <div key={dow} className={d.calendar.dayHeader}>{dow}</div>
                        ))}
                    </div>

                    {/* Day cells */}
                    <div className={d.calendar.grid}>
                        {calendarDays.map((dayObj, idx) => {
                            const dayPosts = dayObj.current ? (postsByDay[dayObj.day] || []) : [];
                            const today = dayObj.current && isToday(currentMonth.year, currentMonth.month, dayObj.day);

                            return (
                                <div key={idx} className={d.calendar.day}>
                                    <div className={today ? d.calendar.dayToday : (dayObj.current ? d.calendar.dayNumber : d.calendar.dayOther)}>
                                        {dayObj.day}
                                    </div>
                                    <div className="flex flex-wrap gap-0.5">
                                        {dayPosts.map((post) =>
                                            (post.platforms || []).map((platform) => (
                                                <span
                                                    key={`${post._id}-${platform}`}
                                                    className={`${d.calendar.postDot} ${PLATFORM_DOT_COLOR[platform] || 'bg-gray-400'}`}
                                                    title={truncateText(post.content, 40)}
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Empty State (list view only) */}
            {!loading && viewMode === 'list' && posts.length === 0 && (
                <div className={d.emptyState.container}>
                    <div className={d.emptyState.iconBox}>
                        <Iconify icon="eva:file-text-outline" width={28} className="text-violet-600" />
                    </div>
                    <h3 className={d.emptyState.title}>Nenhuma publicação encontrada</h3>
                    <p className={d.emptyState.subtitle}>
                        Crie sua primeira publicação para começar a gerenciar seu conteúdo nas redes sociais.
                    </p>
                    <span className={d.emptyState.action} onClick={() => setDrawerOpen(true)}>
                        Criar primeira publicação
                    </span>
                </div>
            )}

            {/* Drawer */}
            <MktPostDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onSave={handleDrawerSave}
            />
        </div>
    );
}
